import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isBefore, parseISO, differenceInCalendarDays } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useClasses } from '../../hooks/useClasses';
import { useTeacher } from '../../hooks/useTeacher';
import {
  AssigneeResponse,
  TaskPriority,
  TaskResponse,
  TaskStatus,
  getTaskById,
  getTasksForTeacher,
  markTaskComplete,
} from '../../api/tasks';
import { useError } from '../../contexts/ErrorContext';
import { useSessionClass } from '../../contexts/SessionClassContext';

type FilterTab = 'all' | 'pending' | 'completed' | 'late' | 'cancelled';

const priorityColor = (priority: TaskPriority): 'default' | 'info' | 'warning' | 'error' => {
  switch (priority) {
    case 'HIGH':
      return 'error';
    case 'MEDIUM':
      return 'info';
    case 'LOW':
    default:
      return 'default';
  }
};

const assigneeStatusChip = (
  task: TaskResponse,
  assignee?: AssigneeResponse
): { label: string; color: 'default' | 'warning' | 'success' | 'error' } => {
  if (task.status === 'CANCELLED') {
    return { label: 'CANCELLED', color: 'default' };
  }

  if (assignee?.assigneeStatus === 'COMPLETED' && assignee.late) {
    return { label: 'LATE SUBMISSION', color: 'error' };
  }

  if (assignee?.assigneeStatus === 'COMPLETED') {
    return { label: 'COMPLETED', color: 'success' };
  }

  return { label: assignee?.assigneeStatus ?? task.status, color: 'warning' };
};

const parseBackendDate = (dateVal: any): Date => {
  if (!dateVal) return new Date();
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return new Date(year, month - 1, day);
  }
  if (typeof dateVal === 'string') {
    return parseISO(dateVal);
  }
  return new Date(dateVal);
};

const formatDate = (value: any) => {
  if (!value) return '-';
  try {
    const date = parseBackendDate(value);
    return format(date, 'dd MMM yyyy');
  } catch {
    return String(value);
  }
};

const getDueDateColor = (endDate: any): string => {
  try {
    const due = parseBackendDate(endDate);
    const today = new Date();
    if (isBefore(due, today) && due.toDateString() !== today.toDateString()) return '#d32f2f';
    if (differenceInCalendarDays(due, today) <= 3) return '#ed6c02';
  } catch (e) {
    console.error(e);
  }
  return 'text.secondary';
};

const normalizeClassName = (value?: string) => (value ?? '').trim().toLowerCase();

const formatSelectedClassName = (name?: string, section?: string) =>
  [name, section].filter(Boolean).join(' ').trim();

const MyTasksPage: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { selectedClass } = useSessionClass();
  const { useStaffByEmail } = useTeacher(user?.schoolId ?? undefined);
  const { classesQuery } = useClasses(user?.schoolId ?? undefined);
  const classes = useMemo(() => classesQuery.data ?? [], [classesQuery.data]);

  const staffQuery = useStaffByEmail(user?.email);
  const teacherId = staffQuery.data?.id;

  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [activeAssignee, setActiveAssignee] = useState<AssigneeResponse | null>(null);
  const [activeTask, setActiveTask] = useState<TaskResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'teacher', teacherId],
    queryFn: async () => {
      const response = await getTasksForTeacher(teacherId!);
      return response.data;
    },
    enabled: !!teacherId,
    // Poll every 30 seconds so newly assigned tasks appear automatically
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  React.useEffect(() => {
    const taskId = (location.state as { taskId?: number } | null)?.taskId;
    if (taskId) {
      // Force refetch of the task list when arriving from a notification
      queryClient.invalidateQueries({ queryKey: ['tasks', 'teacher', teacherId] });
      getTaskById(taskId)
        .then((res) => {
          setSelectedTask(res.data);
          setDetailOpen(true);
        })
        .catch(() => showError('Failed to load task details'));
    }
  }, [location.state, showError, queryClient, teacherId]);

  const getTeacherAssignee = (task: TaskResponse): AssigneeResponse | undefined =>
    task.assignees?.find((a) => a.teacherId === teacherId);

  const filteredTasks = useMemo(() => {
    let tasks = tasksQuery.data ?? [];

    if (selectedClass && selectedClass.section !== '__CLASS_ALL__' && selectedClass.id !== undefined) {
      if (selectedClass.section === '__GRADE_ALL__') {
        tasks = tasks.filter((task) => {
          const taskClass = classes.find((cls) => cls.id === task.classId);
          const selectedClassName = normalizeClassName(selectedClass.name);
          if (taskClass) {
            return normalizeClassName(taskClass.name) === selectedClassName;
          }
          return normalizeClassName(task.className).startsWith(selectedClassName);
        });
      } else {
        const selectedClassLabel = normalizeClassName(
          formatSelectedClassName(selectedClass.name, selectedClass.section)
        );
        tasks = tasks.filter((task) => {
          if (task.classId === selectedClass.id) return true;
          return normalizeClassName(task.className) === selectedClassLabel;
        });
      }
    }

    if (filterTab === 'all') return tasks;
    if (filterTab === 'pending') {
      return tasks.filter(
        (task) => task.status !== 'CANCELLED' && getTeacherAssignee(task)?.assigneeStatus === 'PENDING'
      );
    }
    if (filterTab === 'completed') {
      return tasks.filter((task) => {
        const assignee = getTeacherAssignee(task);
        return task.status !== 'CANCELLED' && assignee?.assigneeStatus === 'COMPLETED' && !assignee.late;
      });
    }
    if (filterTab === 'late') {
      return tasks.filter((task) => {
        const assignee = getTeacherAssignee(task);
        return task.status !== 'CANCELLED' && assignee?.assigneeStatus === 'COMPLETED' && assignee.late;
      });
    }
    return tasks.filter((task) => task.status === 'CANCELLED');
  }, [tasksQuery.data, filterTab, teacherId, selectedClass, classes]);

  const openDetails = async (task: TaskResponse) => {
    try {
      const response = await getTaskById(task.id);
      setSelectedTask(response.data);
      setDetailOpen(true);
    } catch {
      showError('Failed to load task details');
    }
  };

  const openCompleteDialog = (task: TaskResponse) => {
    const assignee = getTeacherAssignee(task);
    if (!assignee) return;
    setActiveTask(task);
    setActiveAssignee(assignee);
    setCompletionNote('');
    setCompleteOpen(true);
  };

  const handleComplete = async () => {
    if (!activeAssignee) return;
    setSubmitting(true);
    try {
      const response = await markTaskComplete(activeAssignee.id, completionNote);
      const completedAssignee = response.data;
      
      const completedTaskId = activeTask?.id;

      // Optimistically update query cache immediately so UI updates instantly
      queryClient.setQueryData<TaskResponse[]>(['tasks', 'teacher', teacherId], (oldTasks) => {
        if (!oldTasks) return oldTasks;
        return oldTasks.map((t) => {
          if (t.id === completedTaskId) {
            const updatedAssignees: AssigneeResponse[] = (t.assignees || []).map((a) => {
              if (a.teacherId === teacherId) {
                return {
                  ...a,
                  assigneeStatus: 'COMPLETED' as const,
                  completionNote: completedAssignee.completionNote ?? completionNote,
                  late: completedAssignee.late,
                };
              }
              return a;
            });
            const allCompleted = updatedAssignees.every((a) => a.assigneeStatus === 'COMPLETED');
            return {
              ...t,
              status: (allCompleted ? 'COMPLETED' : 'IN_PROGRESS') as TaskStatus,
              assignees: updatedAssignees,
            };
          }
          return t;
        });
      });

      showSuccess('Task marked as complete');
      setCompleteOpen(false);
      setSelectedTask(null);

      // Delay a single background refetch by 1000ms to allow backend transaction commit & avoid racing with optimistic update
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tasks', 'teacher', teacherId] });
        queryClient.invalidateQueries({ queryKey: ['tasks', 'school'] });
      }, 1000);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Failed to complete task');
    } finally {
      setSubmitting(false);
    }
  };

  if (staffQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        My Assigned Tasks
      </Typography>

      <Tabs value={filterTab} onChange={(_, value: FilterTab) => setFilterTab(value)} sx={{ mb: 3 }}>
        <Tab label="All" value="all" />
        <Tab label="Pending" value="pending" />
        <Tab label="Completed" value="completed" />
        <Tab label="Late Submission" value="late" />
        <Tab label="Cancelled" value="cancelled" />
      </Tabs>

      {tasksQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Typography color="text.secondary">No tasks found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredTasks.map((task) => {
            const assignee = getTeacherAssignee(task);
            const statusChip = assigneeStatusChip(task, assignee);
            const dueColor = getDueDateColor(task.endDate);
            return (
              <Grid item xs={12} md={4} key={task.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {task.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Assigned by {task.assignedByName}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Chip label={task.className} size="small" variant="outlined" />
                      {assignee?.subjectName && (
                        <Chip label={assignee.subjectName} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: dueColor, mb: 1 }}>
                      Due: {formatDate(task.endDate)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={task.priority} size="small" color={priorityColor(task.priority)} />
                      <Chip
                        label={statusChip.label}
                        size="small"
                        color={statusChip.color}
                      />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    {task.status !== 'CANCELLED' && assignee?.assigneeStatus === 'PENDING' && (
                      <Button size="small" variant="contained" onClick={() => openCompleteDialog(task)}>
                        Mark Complete
                      </Button>
                    )}
                    <Button size="small" variant="outlined" onClick={() => openDetails(task)}>
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTask?.title}</DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedTask.description}
              </Typography>
              <Typography variant="body2">Class: {selectedTask.className}</Typography>
              <Typography variant="body2">Assigned by: {selectedTask.assignedByName}</Typography>
              <Typography variant="body2">
                Due: {formatDate(selectedTask.endDate)}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Task Complete</DialogTitle>
        <DialogContent>
          <TextField
            label="Completion Note"
            fullWidth
            multiline
            rows={3}
            value={completionNote}
            onChange={(e) => setCompletionNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleComplete} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTasksPage;
